import React, {useEffect, useMemo, useState, useCallback} from "react"
import dayjs from "dayjs"
import {DataEmissionPanel} from "./MemoriaCalculo/components/data-emission/data-emission-panel"
import {Box} from "@mui/system"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import {Grid, Paper} from "@mui/material"
import theme from "../../theme/theme"
import DatalakeHeader from "./MemoriaCalculo/components/Header"
import EmissionTabsWrappedLabel from "./MemoriaCalculo/components/emission-tab/emission-tab"
import {ContrapartesPanel} from "./MemoriaCalculo/components/emission-contrapartes/contrapartes-panel"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import GenericModal from "src/v2/components/modal/generic-modal"
import {
    BoxCreditEmissionButtonGroupAdmin
} from "./MemoriaCalculo/components/box-admin-buttons-datalake-emission/box-admin-datalake-emission-admin-buttons"
import {EmissionSummarySections} from "./MemoriaCalculo/components/emission-sections/emission-summary-sections"
import {COLUMNS_MODAL_TABLE_CETIP_ISIN} from "./MemoriaCalculo/constants/full-list-cetip-isin.const"
import LoaderOverlay from "src/v2/components/LoaderOverlay/loader-overlay"
import Loader from "src/v2/components/Loader/loader"
import {useCetipModal} from "./MemoriaCalculo/hooks/use-cetip-isin-modal"
import {TokenService} from "src/services/token-service"
import EmissionEmptyState from "./MemoriaCalculo/components/emission-empty-state/emission-empty-state"
import PropTypes from "prop-types"
import {MotorEmissaoService} from "../../../services/motor-emissao-service"
import EmissaoService from "../../../services/emissao-service"
import {useParameterMotor} from "./MemoriaCalculo/hooks/use-parameter-motor-price"
import {useMemoriaCalculo} from "./MemoriaCalculo/hooks/table-memoria-calculo-hooks/use-memoria-calculo"
import {MemoriaCalculoPanel} from "./MemoriaCalculo/components/data-memoria-calculo/data-memoria-calculo-panel"
import {dispatchSnackbar} from "src/v2/components/SnackBar/snackbar-bus"
import {DATE_FORMAT} from "src/utils/constants"
import DocumentsTab from "./MemoriaCalculo/components/data-documents/documents-tab"
import RegisterDocumentService from "src/services/register-document-service"
import DatePickerSelectV2 from "../../components/Date/date-picker-select"

const SectionPaper = ({children, sx}) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: theme.palette.background.paper,
            borderRadius: 0,
            ...sx,
        }}
    >
        {children}
    </Paper>
)

SectionPaper.propTypes = {
    children: PropTypes.node.isRequired,
    sx: PropTypes.object,
}

const MemoriaCalculoV2 = () => {
    const emissaoMotorService = useMemo(() => new MotorEmissaoService(), [])
    const emissaoDatalakeService = useMemo(() => new EmissaoService(), [])
    const documentService = useMemo(() => new RegisterDocumentService(), [])

    const tokenService = useMemo(() => new TokenService(), [])

    const isAdminUser = tokenService.verifyUserIsAdmin()

    const [activeTab, setActiveTab] = useState("emission")

    const [characterData, setCharacterData] = useState({})
    const [contraparteData, setContraparteData] = useState({})

    const [isLoadingCharacters, setIsLoadingCharacters] = useState(false)
    const [garantiasData, setGarantiasData] = useState({})
    const [offerData, setOfferData] = useState({})
    const [fatosRelevantesData, setFatosRelevantesData] = useState([])
    const pagination = {page: 0}

    const [memoriaCalculoDate, setMemoriaCalculoDate] = useState(null)

    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false)

    const {
        state,
        handleOpenModal,
        handleRowClick,
        callBackOnChangeInputHeader,
        dispatch,
    } = useCetipModal(emissaoMotorService)

    const {actions: parameterActions} = useParameterMotor(emissaoMotorService, state.rendaFixaCaracteristicasID)
    const {state: memoriaCalculoState, actions: memoriaCalculoActions} = useMemoriaCalculo()

    const handleTabChange = useCallback((event, newValue) => {
        setActiveTab(newValue)
    }, [])

    const handleMemoriaCalculoDateChange = useCallback((date) => {
        if (!date || !dayjs.isDayjs(date) || !date.isValid()) return

        setMemoriaCalculoDate(date.format(DATE_FORMAT))
    }, [])

    const handleExportExcel = useCallback(async () => {
        try {
            const cetipIsinExport =
                state.selectCetipIsin ||
                memoriaCalculoState.parametrosData?.CETIP ||
                memoriaCalculoState.parametrosData?.ISIN

            const exported = await memoriaCalculoActions.exportExcel(cetipIsinExport)

            if (!exported) {
                dispatchSnackbar(
                    "Não há dados suficientes para exportar o arquivo.",
                    "warning"
                )
                return
            }

            dispatchSnackbar("Download do arquivo iniciado.", "success")
        } catch (error) {
            console.error("Erro ao exportar Excel:", error)
            dispatchSnackbar("Erro ao exportar Excel.", "error")
        }
    }, [
        memoriaCalculoActions,
        state.selectCetipIsin,
        memoriaCalculoState.parametrosData?.CETIP,
        memoriaCalculoState.parametrosData?.ISIN,
    ])

    const loadGarantias = useCallback(async (cetip) => {
        try {
            const response = await emissaoMotorService.getGarantias(cetip)

            setGarantiasData(
                response?.body?.data ||
                response?.body ||
                {}
            )
        } catch {
            setGarantiasData({})
        }
    }, [emissaoMotorService])

    const loadTipoOferta = useCallback(async (cetipIsin) => {
        try {
            const response = await emissaoMotorService.getTipoOferta(cetipIsin)

            setOfferData(
                response?.body?.data ||
                response?.body ||
                {}
            )
        } catch {
            setOfferData({})
        }
    }, [emissaoMotorService])

    const loadFatosRelevantes = useCallback(async (cetipIsin) => {
        try {
            const response = await emissaoMotorService.getFatosRelevantes(cetipIsin)

            const data = response?.body?.data || response?.body || []

            setFatosRelevantesData(Array.isArray(data) ? data : [])
        } catch {
            setFatosRelevantesData([])
        }
    }, [emissaoMotorService])

    const loadCharacters = useCallback(async (cetipIsin) => {
        if (!cetipIsin) return

        setIsLoadingCharacters(true)

        try {
            const request = await emissaoMotorService.getEmissaoDetalhe(cetipIsin)

            const caracteristicas = request?.body?.Caracteristicas || {}
            const contrapartes = request?.body?.Contrapartes || {}

            setCharacterData(caracteristicas)
            setContraparteData(contrapartes)

            Promise.allSettled([
                loadGarantias(cetipIsin),
                loadTipoOferta(cetipIsin),
                loadFatosRelevantes(cetipIsin),
            ])
        } catch {
            setCharacterData({})
            setContraparteData({})
            setGarantiasData({})
            setOfferData({})
            setFatosRelevantesData([])
        } finally {
            setIsLoadingCharacters(false)
        }
    }, [emissaoMotorService, loadGarantias, loadTipoOferta, loadFatosRelevantes])

    useEffect(() => {
        if (!state.selectCetipIsin) return

        const loadData = async () => {
            await Promise.allSettled([
                parameterActions.loadParametros(),
                loadCharacters(state.selectCetipIsin),
            ])
        }

        loadData()
    }, [state.selectCetipIsin])

    useEffect(() => {
        if (!state.selectCetipIsin) return

        memoriaCalculoActions.loadMemoriaCalculo(state.selectCetipIsin, {
            dataReferencia: memoriaCalculoDate,
        })
    }, [state.selectCetipIsin, memoriaCalculoDate])

    useEffect(() => {
        if (
            memoriaCalculoState.dataReferencia &&
            memoriaCalculoState.dataReferencia !== memoriaCalculoDate
        ) {
            setMemoriaCalculoDate(memoriaCalculoState.dataReferencia)
        }
    }, [memoriaCalculoState.dataReferencia])

    const isPageLoading =
        state.isLoadingModalData ||
        isLoadingCharacters ||
        isLoadingDocuments

    return (
        <SideBarLayout title="Memória de Cálculo">
            {isPageLoading ? (
                <LoaderOverlay>
                    <Loader/>
                </LoaderOverlay>
            ) : (
                <GenericModal
                    isOpen={state.listFullModalVisible && !state.isLoadingModalData}
                    onClose={() => dispatch({type: "TOGGLE_MODAL"})}
                    title="Lista de CETIP/ISIN"
                    width="65%"
                >
                    <DataGridTable
                        columns={COLUMNS_MODAL_TABLE_CETIP_ISIN}
                        rows={state.rows}
                        pagination={pagination}
                        pageSize={25}
                        disableDeleteColumn
                        onRowClick={handleRowClick}
                        pointerCursor
                    />
                </GenericModal>
            )}

            <Grid
                sx={{
                    background: theme.palette.background.primary,
                    height: "100%",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: {
                            xxxs: 0,
                            lg: 209,
                        },
                        right: 0,
                        zIndex: 0,
                    }}
                >
                    <DatalakeHeader
                        emissaoService={emissaoMotorService}
                        callBackOnChange={callBackOnChangeInputHeader}
                        handleClickFullList={handleOpenModal}
                        externalValue={state.selectCetipIsin}
                        showExportCsv={activeTab === "memoriaCalculo"}
                        onClickExportCsv={handleExportExcel}
                    />
                </Box>

                {!state.selectCetipIsin && Object.keys(characterData).length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            position: "relative",
                            mt: {xxxs: 36, md: 30, lg: 22},
                            width: "100%",
                            background: theme.palette.background.paper,
                            flexDirection: "column",
                        }}
                    >
                        <EmissionEmptyState handleClickFullList={handleOpenModal}/>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            position: "relative",
                            mt: {xxxs: 36, md: 30, lg: 22},
                            width: "100%",
                            background: theme.palette.background.paper,
                            flexDirection: "column",
                        }}
                    >
                        <Box>
                            <EmissionTabsWrappedLabel
                                value={activeTab}
                                onChange={handleTabChange}
                                emissionTabChildren={
                                    <Box
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            background: theme.palette.background.primary,
                                        }}
                                    >
                                        <SectionPaper>
                                            <DataEmissionPanel characterData={characterData}/>
                                        </SectionPaper>

                                        <SectionPaper sx={{mt: 3}}>
                                            <ContrapartesPanel contraparteData={contraparteData}/>

                                            {isAdminUser && (
                                                <SectionPaper sx={{mt: 1}}>
                                                    <BoxCreditEmissionButtonGroupAdmin
                                                        rendaFixaCaracteristicasID={state.rendaFixaCaracteristicasID}
                                                    />
                                                </SectionPaper>
                                            )}

                                            <SectionPaper sx={{mt: 1}}>
                                                <EmissionSummarySections
                                                    cetipIsin={state.selectCetipIsin}
                                                    MotorEmissaoService={emissaoMotorService}
                                                    emissaoDatalakeService={emissaoDatalakeService}
                                                    characterData={characterData}
                                                    contraparteData={contraparteData}
                                                    garantiasData={garantiasData}
                                                    offerData={offerData}
                                                    fatosRelevantesData={fatosRelevantesData}
                                                    admin={isAdminUser}
                                                />
                                            </SectionPaper>
                                        </SectionPaper>
                                    </Box>
                                }
                                memoriaCalculoTabChildren={
                                    <Box
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            background: theme.palette.background.primary,
                                        }}
                                    >
                                        <MemoriaCalculoPanel
                                            cetipIsin={state.selectCetipIsin}
                                            parametersHeader={
                                                <Grid container>
                                                    <Grid item xs={12} md={3} xl={3} sx={{mb: 3}}>
                                                        <DatePickerSelectV2
                                                            value={memoriaCalculoDate}
                                                            label="Data Referência"
                                                            onChange={handleMemoriaCalculoDateChange}/>
                                                    </Grid>
                                                </Grid>
                                            }
                                            data={memoriaCalculoState.data}
                                            hasResultado={memoriaCalculoState.hasResultado}
                                            puparData={memoriaCalculoState.puparData}
                                            puMtmData={memoriaCalculoState.puMtmData}
                                            loadingSections={memoriaCalculoState.loadingSections}
                                        />
                                    </Box>
                                }
                                documentsTabChildren={
                                    <Box
                                        sx={{
                                            height: "100vh",
                                            width: {
                                                lg: "calc(100vw - 300px)",
                                            },
                                            p: 3,
                                        }}
                                    >
                                        <SectionPaper>
                                            <DocumentsTab
                                                isAdmin={isAdminUser}
                                                documentService={documentService}
                                                cetip={state.selectCetipIsin}
                                                setIsLoading={setIsLoadingDocuments}
                                            />
                                        </SectionPaper>
                                    </Box>
                                }
                            />
                        </Box>
                    </Box>
                )}
            </Grid>
        </SideBarLayout>
    )
}

export default MemoriaCalculoV2
